// One ticket's fees, the same math as Payments::FeeCalculator.calculate
// (app/services/payments/fee_calculator.rb) for a single ticket. Used by the
// Founding Organizer fee slider (#2589); the spec pins it to the Ruby numbers.

export interface StripeRate {
  /** e.g. 0.029 */
  percent: number;
  /** e.g. 30 */
  flatCents: number;
}

export interface TicketFeeInput {
  priceCents: number;
  /** Platform fee as a fraction, e.g. 0.05. */
  platformPercent: number;
  /** Per-ticket ceiling on the platform fee; null for none. */
  platformCapCents: number | null;
  /** Organizer absorbs fees (buyer pays the ticket price) vs. buyer pays them on top. */
  absorbFees: boolean;
  stripe: StripeRate;
}

export interface TicketFees {
  platformFeeCents: number;
  stripeFeeCents: number;
  /** What the buyer is charged. */
  buyerPaysCents: number;
  /** What reaches the organizer. */
  organizerKeepsCents: number;
}

const stripeFee = (totalCents: number, stripe: StripeRate) =>
  Math.round(totalCents * stripe.percent + stripe.flatCents);

export function ticketFees({ priceCents, platformPercent, platformCapCents, absorbFees, stripe }: TicketFeeInput): TicketFees {
  if (priceCents <= 0) return { platformFeeCents: 0, stripeFeeCents: 0, buyerPaysCents: 0, organizerKeepsCents: 0 };

  const percentFee = Math.round(priceCents * platformPercent);
  const platformFeeCents = platformCapCents === null ? percentFee : Math.min(percentFee, platformCapCents);

  if (absorbFees) {
    const stripeFeeCents = stripeFee(priceCents, stripe);
    return {
      platformFeeCents,
      stripeFeeCents,
      buyerPaysCents: priceCents,
      organizerKeepsCents: priceCents - platformFeeCents - stripeFeeCents,
    };
  }

  // Stripe's percentage applies to the total, which includes Stripe's fee, so
  // gross up to find the base it's charged on, then itemise (#1115).
  const grossUp = Math.ceil((priceCents + platformFeeCents + stripe.flatCents) / (1 - stripe.percent));
  const stripeFeeCents = stripeFee(grossUp, stripe);
  return {
    platformFeeCents,
    stripeFeeCents,
    buyerPaysCents: priceCents + platformFeeCents + stripeFeeCents,
    organizerKeepsCents: priceCents,
  };
}
